import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  MenuItem,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Stack
} from '@mui/material';
import {
  Close as CloseIcon
} from '@mui/icons-material';
import { ODOSModule } from '../../services/odosService';

const CATEGORIES = [
  'DevSecOps',
  'Security & Compliance',
  'Modernization',
  'Monitoring & Observability',
  'Data Integration',
  'User Experience',
  'Business Process',
  'Data Governance',
  'Infrastructure',
  'Access Management',
  'Collaboration',
  'Quality Assurance',
  'Accessibility',
  'Administration'
];

const STATUSES = [
  'planning',
  'in-development',
  'testing',
  'deployed',
  'active',
  'maintenance',
  'deprecated'
];

interface ModuleEditDialogProps {
  open: boolean;
  onClose: () => void;
  module: ODOSModule | null;
  onSave: (moduleData: any) => Promise<void>;
}

const ModuleEditDialog: React.FC<ModuleEditDialogProps> = ({
  open,
  onClose,
  module,
  onSave
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    status: '',
    pwsSection: '',
    pwsPage: '',
    pwsRequirement: '',
    versionMajor: 1,
    versionMinor: 0,
    versionPatch: 0
  });
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (module && open) {
      setFormData({
        name: module.name || '',
        description: module.description || '',
        category: module.category || '',
        status: module.status || 'planning',
        pwsSection: module.pwsReference?.section || '',
        pwsPage: module.pwsReference?.page?.toString() || '',
        pwsRequirement: module.pwsReference?.requirement || '',
        versionMajor: module.version?.major || 1,
        versionMinor: module.version?.minor || 0,
        versionPatch: module.version?.patch || 0
      });
      setTags(module.tags || []);
    }
  }, [module, open]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Validate required fields
      if (!formData.name.trim()) {
        setError('Module name is required');
        setSaving(false);
        return;
      }
      if (!formData.description.trim()) {
        setError('Description is required');
        setSaving(false);
        return;
      }
      if (!formData.category) {
        setError('Category is required');
        setSaving(false);
        return;
      }

      const moduleData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        category: formData.category,
        status: formData.status,
        pwsReference: {
          section: formData.pwsSection,
          page: formData.pwsPage ? parseInt(formData.pwsPage) : undefined,
          requirement: formData.pwsRequirement
        },
        version: {
          major: formData.versionMajor,
          minor: formData.versionMinor,
          patch: formData.versionPatch
        },
        tags
      };

      await onSave(moduleData);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save module');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="module-edit-dialog-title"
    >
      <DialogTitle id="module-edit-dialog-title">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ color: '#003366', fontFamily: 'Merriweather, serif' }}>
            Edit Module - {module?.name}
          </Typography>
          <IconButton onClick={handleClose} disabled={saving} aria-label="Close dialog">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#003366' }}>
              Basic Information
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Module Name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              fullWidth
              required
              disabled={saving}
              placeholder="e.g., DevSecOps Pipeline & Automation"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              fullWidth
              required
              multiline
              rows={3}
              disabled={saving}
              placeholder="Describe the module's purpose and functionality"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                label="Category"
                disabled={saving}
              >
                {CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                label="Status"
                disabled={saving}
              >
                {STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* PWS Reference */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#003366', mt: 2 }}>
              PWS Reference
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Section"
              value={formData.pwsSection}
              onChange={(e) => handleChange('pwsSection', e.target.value)}
              fullWidth
              disabled={saving}
              placeholder="e.g., 7.5"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Page"
              value={formData.pwsPage}
              onChange={(e) => handleChange('pwsPage', e.target.value)}
              fullWidth
              type="number"
              disabled={saving}
              placeholder="e.g., 70"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Requirement"
              value={formData.pwsRequirement}
              onChange={(e) => handleChange('pwsRequirement', e.target.value)}
              fullWidth
              disabled={saving}
              placeholder="e.g., Accessibility Compliance"
            />
          </Grid>

          {/* Version */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#003366', mt: 2 }}>
              Version
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Major"
              value={formData.versionMajor}
              onChange={(e) => handleChange('versionMajor', parseInt(e.target.value) || 0)}
              fullWidth
              type="number"
              disabled={saving}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Minor"
              value={formData.versionMinor}
              onChange={(e) => handleChange('versionMinor', parseInt(e.target.value) || 0)}
              fullWidth
              type="number"
              disabled={saving}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Patch"
              value={formData.versionPatch}
              onChange={(e) => handleChange('versionPatch', parseInt(e.target.value) || 0)}
              fullWidth
              type="number"
              disabled={saving}
            />
          </Grid>

          {/* Tags */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#003366', mt: 2 }}>
              Tags
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                {tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                    disabled={saving}
                    sx={{ mb: 1 }}
                  />
                ))}
              </Stack>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="Add Tag"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  size="small"
                  fullWidth
                  disabled={saving}
                  placeholder="Type and press Enter"
                />
                <Button
                  onClick={handleAddTag}
                  variant="outlined"
                  disabled={saving || !newTag.trim()}
                  sx={{ borderColor: '#003366', color: '#003366' }}
                >
                  Add
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          sx={{
            bgcolor: '#003366',
            '&:hover': { bgcolor: '#002244' }
          }}
        >
          {saving ? 'Saving...' : 'Save Module'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModuleEditDialog;
